import { CommonModule } from '@angular/common';
import { Component, inject } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatIcon } from '@angular/material/icon';
import { ActivatedRoute, Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { NgxSpinnerService } from 'ngx-spinner';
import { StationService } from '../../../../services/station.service';
import { DeviceService } from '../../../../services/device.service';
import { FNFResponse } from '../../../../models/response';

@Component({
  selector: 'app-station-form',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, MatIcon],
  templateUrl: './station-form.component.html',
  styleUrl: './station-form.component.css',
})
export class StationFormComponent {
  private fb = inject(FormBuilder);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private toastr = inject(ToastrService);
  private spinner = inject(NgxSpinnerService);
  private stationService = inject(StationService);
  private deviceService = inject(DeviceService);

  /** Set on the edit route; null when adding. */
  station: any = null;
  allStations: any[] = [];
  loading = true;

  isDuplicate = false;
  /** A second default of the same type replaces the first; warn before saving. */
  displayDuplicateDefault = false;

  stationTypes: { displayName: string; value: string }[] = [];
  selectedType = '';
  printers: any[] = [];

  stationForm: FormGroup = this.buildForm(null);

  ngOnInit() {
    this.route.paramMap.subscribe((params) => this.load(params.get('id')));
  }

  private load(stationId: string | null) {
    this.loading = true;
    this.stationService.getStations().subscribe({
      next: (response: FNFResponse<any>) => {
        this.allStations = response?.data || [];
        this.station = stationId
          ? this.allStations.find((s: any) => String(s.station_id) === String(stationId)) || null
          : null;

        this.stationForm = this.buildForm(this.station);
        this.buildTypeOptions();
        this.applyDefaultRules();
        this.loading = false;
      },
      error: () => {
        this.toastr.error('Unable to load the station. Please try again later.', 'Error');
        this.loading = false;
        this.cancel();
      },
    });

    // Printer options are a convenience; a failure here leaves the field empty
    // rather than blocking the form.
    this.deviceService.getDevices().subscribe({
      next: (response: FNFResponse<any>) => {
        this.printers = (response?.data || []).filter(
          (d: any) => (d.device_type || '').toLowerCase() === 'printer',
        );
      },
      error: () => {
        this.printers = [];
      },
    });
  }

  private buildForm(station: any): FormGroup {
    return this.fb.group({
      station_name: [
        station?.station_name ?? '',
        [Validators.required, Validators.minLength(3), Validators.maxLength(50)],
      ],
      description: [station?.description ?? '', [Validators.maxLength(250)]],
      station_type: [station?.station_type ?? '', [Validators.required]],
      is_default: [station?.is_default ?? false],
      assigned_printer: [station?.assigned_printer ?? ''],
      ip_address: [station?.ip_address ?? '', [Validators.pattern(/^(\d{1,3}\.){3}\d{1,3}$/)]],
      network_display_name: [station?.network_display_name ?? '', [Validators.maxLength(50)]],
    });
  }

  // ------------------------------------------------------------------ types

  /**
   * Counter and Food Runner are single-instance station types, so they are
   * offered only while unused — or while this station already holds them.
   */
  private buildTypeOptions() {
    const taken = (type: string) =>
      this.allStations.some(
        (s: any) => s.station_type === type && s.station_id !== this.station?.station_id,
      );

    this.stationTypes = [{ displayName: 'Kitchen', value: 'kitchen' }];
    if (!taken('food_runner')) {
      this.stationTypes.push({ displayName: 'Food Runner', value: 'food_runner' });
    }
    if (!taken('counter')) {
      this.stationTypes.push({ displayName: 'Counter', value: 'counter' });
    }

    const current = this.stationForm.get('station_type')?.value;
    this.selectedType = this.stationTypes.find((t) => t.value === current)?.displayName ?? '';

    // A lone option is not a choice; pre-select it when adding.
    if (!this.station && this.stationTypes.length === 1) {
      this.selectType(this.stationTypes[0].value);
    }
  }

  /** The one existing default of a type cannot be un-defaulted from here. */
  private applyDefaultRules() {
    const control = this.stationForm.get('is_default');
    if (this.station?.is_default) {
      control?.disable();
    } else {
      control?.enable();
    }
  }

  selectType(value: string) {
    this.selectedType = this.stationTypes.find((t) => t.value === value)?.displayName ?? '';
    this.stationForm.get('station_type')?.setValue(value);

    // Counter and Food Runner are unique, so they are always their own default.
    // A first Kitchen is too, but a later one only when none is set yet.
    const control = this.stationForm.get('is_default');
    if (value === 'counter' || value === 'food_runner') {
      control?.setValue(true);
    } else if (value === 'kitchen') {
      const hasDefaultKitchen = this.allStations.some(
        (s: any) =>
          s.station_type === 'kitchen' && s.is_default && s.station_id !== this.station?.station_id,
      );
      control?.setValue(!hasDefaultKitchen);
    }
    this.checkDefaultClash();
  }

  checkDefaultClash() {
    const isDefault = this.stationForm.get('is_default')?.value;
    this.displayDuplicateDefault =
      !!isDefault &&
      this.stationForm.get('station_type')?.value === 'kitchen' &&
      this.allStations.some(
        (s: any) =>
          s.station_type === 'kitchen' && s.is_default && s.station_id !== this.station?.station_id,
      );
  }

  // ----------------------------------------------------------------- submit

  checkDuplicate() {
    const name = (this.stationForm.get('station_name')?.value || '').trim().toLowerCase();
    this.isDuplicate = this.allStations.some(
      (s: any) =>
        s.station_name?.trim().toLowerCase() === name && s.station_id !== this.station?.station_id,
    );
  }

  cancel() {
    this.router.navigate(['/main/station']);
  }

  save() {
    if (this.stationForm.invalid || this.isDuplicate) {
      return;
    }

    // getRawValue so a disabled is_default still reaches the API.
    const value = this.stationForm.getRawValue();
    const payload: any = {
      station_name: value.station_name,
      station_type: value.station_type,
      is_default: value.is_default,
    };
    // The connectivity fields are not part of the stations API yet, so they are
    // sent only once filled in and existing requests stay byte-identical.
    for (const key of ['description', 'assigned_printer', 'ip_address', 'network_display_name']) {
      if ((value[key] || '').trim()) {
        payload[key] = value[key].trim();
      }
    }

    this.spinner.show();
    const request = this.station
      ? this.stationService.editStation(payload, this.station.station_id)
      : this.stationService.addStation(payload);

    request.subscribe({
      next: (response: FNFResponse<any>) => {
        this.spinner.hide();
        if (!response.data) {
          const message = response.error?.error?.message || response.error?.message;
          this.toastr.error(message || 'No data found!', 'Error');
          return;
        }
        this.toastr.success(
          this.station ? 'Station updated successfully' : 'Station added successfully',
          'Success',
        );
        this.cancel();
      },
      error: () => {
        this.spinner.hide();
        this.toastr.error('Unable to complete your action. Please try again later.', 'Error');
      },
    });
  }
}
